import React, { useState } from 'react';
import CornerPanel from '../components/CornerPanel';
import { Activity, Trophy, BookOpen, Cpu, Zap, DollarSign, ShieldAlert } from 'lucide-react';

/**
 * Iter 110 — Operating Manual, operator-first rewrite.
 *
 * Prior revision was dense and mixed stock + prop concepts in the same
 * paragraphs. This rewrite answers one question per section: "What do
 * I do, right now, on THIS tab, to make money with ATDE?" — kept in
 * the house cyberpunk voice but each step is a direct action.
 *
 * Structure:
 *   • The 5-step money loop (always visible)         ← operator does this
 *   • Stock Engine (tab)   — Dashboard · Discovery · Portfolio
 *   • Alt Markets (tab)    — Prop board · Best plays · Entry builder
 *   • System Flow (tab)    — how signals + learning work (background)
 *   • Definitions (tab)    — short glossary
 */

const MoneyLoop = () => (
  <div
    data-testid="manual-money-loop"
    className="border border-cyan-sys/50 p-3 my-4 bg-[rgba(8,247,254,0.04)]"
  >
    <div className="text-[11px] text-cyan-sys uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
      <DollarSign size={13} /> // the 5-step money loop
    </div>
    <ol className="space-y-2 text-[12px] font-mono text-[var(--txt-primary)]">
      <li className="flex gap-3">
        <span className="text-cyan-sys shrink-0">01</span>
        <span>
          <span className="text-cyan-sys">DISCOVERY</span> → run a scan. Only promote rows
          graded <span className="text-buy">A+ or A</span>. B/C are watch-only.
        </span>
      </li>
      <li className="flex gap-3">
        <span className="text-cyan-sys shrink-0">02</span>
        <span>
          <span className="text-cyan-sys">DASHBOARD</span> → promote the A+ candidate to your
          Watchlist (max 5). Confirm the AI Decision panel shows{' '}
          <span className="text-buy">BUY</span> or <span className="text-hold">HOLD</span>, not WAIT.
        </span>
      </li>
      <li className="flex gap-3">
        <span className="text-cyan-sys shrink-0">03</span>
        <span>
          <span className="text-cyan-sys">ENTRY ZONE</span> → only buy inside the green safe band.
          Default risk <span className="text-hold">2% equity</span>. Set your stop AT the firewall-
          suggested level; set your target at the Exit Risk guidance.
        </span>
      </li>
      <li className="flex gap-3">
        <span className="text-cyan-sys shrink-0">04</span>
        <span>
          <span className="text-cyan-sys">PORTFOLIO</span> → log the position. Watch Exit Risk live;
          when the score crosses <span className="text-sell">65</span>, scale out 50% and trail the rest.
        </span>
      </li>
      <li className="flex gap-3">
        <span className="text-cyan-sys shrink-0">05</span>
        <span>
          <span className="text-cyan-sys">CLOSE</span> → when you exit, pick{' '}
          <span className="text-buy">save to learning</span> (default) so the system learns from
          the result. Review the entry in <span className="text-cyan-sys">LEARNING / TRUTH</span> next day.
        </span>
      </li>
    </ol>
  </div>
);

const Section = ({ num, title, action, icon: Icon, children }) => (
  <div className="mb-5">
    <div className="font-display uppercase tracking-[0.3em] text-[12px] text-cyan-sys glow-cyan border-b border-[var(--border-default)] pb-2 mb-2 flex items-center gap-2">
      {Icon && <Icon size={13} />} §{num} — {title}
    </div>
    {action && (
      <div className="border-l-2 border-buy pl-2 py-1 mb-2 bg-[rgba(0,255,128,0.04)]">
        <div className="text-[9px] text-buy tracking-[0.3em] uppercase mb-0.5">▸ do this</div>
        <div className="text-[11px] text-[var(--txt-primary)] font-mono leading-relaxed">
          {action}
        </div>
      </div>
    )}
    <div className="text-[11px] leading-[1.7] text-[var(--txt-secondary)] space-y-1.5 font-mono">
      {children}
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex gap-2"><span className="text-cyan-sys shrink-0">▸</span><span>{children}</span></div>
);

// ─────────────────────────────────────────────────────────────────────
// Definitions · Pipeline · Non-negotiable rules
// ─────────────────────────────────────────────────────────────────────

const Definitions = () => {
  const items = [
    ['A+ GRADE',   'Discovery confluence ≥80. Highest-trust BUY candidate.'],
    ['A GRADE',    '70-79. Solid, trade with normal size.'],
    ['B / C',      '<70. Watch only — do not open a position.'],
    ['EDGE %',     '>5% = thin · ≥10% = strong · ≥20% verify data (prop markets).'],
    ['RISK %',     'Position size = equity × risk% ÷ stop distance. Default 2%, hard block 7%.'],
    ['EXIT RISK',  '0-100 score. >65 = decline detected — scale out 50% and trail.'],
    ['FRESH',      'Watchlist tick age ≤30s. YELLOW = aging. RED = stale, wait.'],
    ['CONFLUENCE', 'Weighted sum of trend / momentum / volume / regime / MTF alignment.'],
    ['LEARNING',   'Closed trades feed weight updates (≥20 samples needed).'],
    ['SAVE MODE',  'Learning = feeds learning + truth · Unfinished = isolated · Discard = ignored.'],
  ];
  return (
    <div
      className="border border-cyan-sys/30 p-3 my-4 bg-[rgba(8,247,254,0.03)]"
      data-testid="manual-definitions"
    >
      <div className="text-[10px] text-cyan-sys uppercase tracking-[0.3em] mb-2">// definitions</div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[11px] font-mono">
        {items.map(([term, def]) => (
          <div key={term} className="flex gap-2">
            <dt className="min-w-[100px] text-cyan-sys uppercase tracking-wider shrink-0">{term}</dt>
            <dd className="text-[var(--txt-secondary)]">{def}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const NonNegotiableRules = () => {
  const rules = [
    'Only trade A+ / A grade Discovery rows. B/C stay on watch.',
    'Stay inside the Entry Zone safe band. Never chase.',
    'Default 2% risk. Warn at 3%. Hard block at 7%.',
    'Exit Risk > 65 → scale out 50%, trail the rest.',
    'If the Firewall shows DEGRADED or STALE → wait, do not enter.',
    'Failure Guard engaged → stop opening new positions, review Learning.',
    'Every close picks a save mode. Default to Learning so the system improves.',
  ];
  return (
    <div
      className="border border-buy/40 p-3 my-4 bg-[rgba(0,255,128,0.03)]"
      data-testid="manual-rules"
    >
      <div className="text-[10px] text-buy uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
        <ShieldAlert size={11} /> // non-negotiable rules
      </div>
      <ul className="space-y-1 text-[11px] font-mono">
        {rules.map((r, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-buy shrink-0">{(i + 1).toString().padStart(2, '0')}</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Content tabs
// ─────────────────────────────────────────────────────────────────────

const StockEngine = () => (
  <>
    <Section
      num="01"
      icon={Activity}
      title="Dashboard — your trading cockpit"
      action={<>Keep ≤5 symbols on the Watchlist. Only act when AI Decision reads <span className="text-buy">BUY</span>. Check the Exit Risk score before closing.</>}
    >
      <p>The Dashboard shows your Watchlist, live chart, AI Decision, firewall + validation,
         Entry Zone and Exit Risk.</p>
      <Tip>Watchlist freshness: <span className="text-buy">green</span> = fresh ·{' '}
        <span className="text-hold">yellow</span> = aging · <span className="text-sell">red</span> = stale (wait).</Tip>
      <Tip>AI Decision resolves through 4 cache tiers — never blocks &gt; 150ms. If it stalls, refresh.</Tip>
      <Tip>Entry Zone has a green safe band. If price is outside it, you're <span className="text-sell">chasing</span> — skip.</Tip>
    </Section>

    <Section
      num="02"
      icon={Zap}
      title="Discovery — find high-grade candidates"
      action={<>Click <span className="text-cyan-sys">RUN SCAN</span>. Promote only <span className="text-buy">A+</span> or <span className="text-buy">A</span> rows to the Watchlist.</>}
    >
      <p>Discovery scans 30 symbols using learned weights and surfaces BUY candidates with
         confluence gauges (0-100).</p>
      <Tip>≥80 = A+ · 70-79 = A · &lt;70 = pass.</Tip>
      <Tip>One-click <span className="text-cyan-sys">promote</span> adds the symbol to your Watchlist.</Tip>
      <Tip>Mobile: tap the <span className="text-cyan-sys">FILTERS</span> button to filter by grade or momentum stage.</Tip>
    </Section>

    <Section
      num="03"
      icon={DollarSign}
      title="Entry / Exit — do not improvise"
      action={<>Buy inside the Entry Zone safe band only. Place your stop at the firewall level. Target = Exit Risk guidance.</>}
    >
      <p>Stock entries respect: regime lock, density, noise suppression, MTF alignment,
         volume consistency, and breakout/fakeout detection.</p>
      <Tip>Exit Risk &gt; 65 = decline detected → scale out 50%, trail the rest.</Tip>
      <Tip>Firewall says DEGRADED → wait for cooldown. Never override.</Tip>
    </Section>

    <Section
      num="04"
      icon={ShieldAlert}
      title="Portfolio — track + learn"
      action={<>After every close, pick <span className="text-buy">save to learning</span>. Only pick Unfinished if data was incomplete.</>}
    >
      <p>Position size = equity × risk% ÷ stop distance. Default 2%. Risk grows with equity.</p>
      <Tip>Above 3% → UI warning. Above 7% → hard block.</Tip>
      <Tip>Failure Guard halts new entries after a loss streak — honor it.</Tip>
      <Tip>The <span className="text-cyan-sys">Recent Openings</span> and <span className="text-cyan-sys">Recent Closes</span> feeds show your last 10 events at a glance.</Tip>
    </Section>
  </>
);

const AltMarketsEngine = () => (
  <>
    <Section
      num="05"
      icon={Trophy}
      title="Prop Board — real projections, not vibes"
      action={<>Trade <span className="text-buy">T1</span> stat types (points, rebounds, assists) first. Avoid T3 unless edge is strong.</>}
    >
      <Tip>T1 → +10% confidence multiplier.</Tip>
      <Tip>T3 (3pm, steals, blocks, turnovers) → excluded unless clear edge.</Tip>
      <Tip>Every row carries edge%, model%, implied%, line move, σ, confidence, slate phase, risk, pick.</Tip>
      <Tip>W.AVG = "—" means data missing. The row dims — skip it.</Tip>
    </Section>

    <Section
      num="06"
      icon={Zap}
      title="Best Plays — top-5 auto-selector"
      action={<>Use <span className="text-buy">HOT</span> rows first. Fade or pass <span className="text-sell">FAKEOUT</span>. Size down <span className="text-hold">RISK</span>.</>}
    >
      <p>Score = edge 40% · confidence 20% · signal 15% · low-noise 10% · line-move 10% · sharp 5%.
         Slate phase adds +6 (PRE-MARKET) or +3 (EARLY).</p>
      <Tip>HOT = composite ≥80. STRONG = edge ≥10%.</Tip>
      <Tip>PRE-MARKET = early inefficiency, size 0.5× normal.</Tip>
    </Section>

    <Section
      num="07"
      icon={Activity}
      title="Entry Builder — compose a parlay"
      action={<>Keep legs to 2-3. 4+ legs only if every leg has &gt;10% edge.</>}
    >
      <p>Click actionable rows to build. The builder reports combined confidence, SAFE/BALANCED/
         HIGH-RISK classification, and penalizes same-team correlation.</p>
      <Tip>SAFE / BALANCED = fine. HIGH-RISK → drop a leg.</Tip>
    </Section>

    <Section
      num="08"
      icon={Trophy}
      title="Performance Tracker — read, don't guess"
      action={<>Trust buckets with ≥30 resolves. Smaller samples are noise.</>}
    >
      <Tip>Win rate split by stat-type and confidence bucket.</Tip>
      <Tip>Failure Guard auto-engages after a loss streak — pause and review.</Tip>
    </Section>
  </>
);

const SystemFlow = () => {
  const steps = ['Data Stack', 'Slate Engine', 'Alt Predictions', 'Best Plays', 'Entry Builder', 'Execution', 'Learning Loop'];
  return (
    <>
      <div className="border border-cyan-sys/30 p-3 my-4 bg-[rgba(8,247,254,0.03)]" data-testid="manual-pipeline">
        <div className="text-[10px] text-cyan-sys uppercase tracking-[0.3em] mb-2">// pipeline</div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <span className="px-2 py-1 border border-cyan-sys/40 text-cyan-sys">{s}</span>
              {i < steps.length - 1 && <span className="text-[var(--txt-muted)]">→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <Section num="09" icon={Cpu} title="How signals are generated">
        <p>Each tick resolves through 4 cache tiers: HOT (&lt;20ms) → SEMI-RECENT (&lt;200ms) → live
           AI (Claude Sonnet 4.5 with multi-model cascade) → deterministic fallback. UI is bound to a 150ms ceiling.</p>
        <Tip>"DEGRADED MODE" pill → Finnhub breaker open. Wait for cooldown.</Tip>
        <Tip>If the primary AI is rate-limited, cascade falls through Haiku → Gemini → GPT-5.4 silently.</Tip>
      </Section>
      <Section num="10" icon={BookOpen} title="Future Slate Engine (Alt Markets)">
        <p>Every 6h pulls next 72h NBA games from ESPN, scores matchup balance + volatility,
           and classifies LIVE / EARLY / PRE-MARKET. Zero hallucination — pure truth.</p>
        <Tip>HIGH-VOL SLATE badge → injury-driven distribution; consider UNDERS.</Tip>
      </Section>
      <Section num="11" icon={Activity} title="When NOT to trade">
        <Tip>Failure Guard engaged.</Tip>
        <Tip>System Pulse SLOW or DEGRADED.</Tip>
        <Tip>Best Plays in fallback mode.</Tip>
        <Tip>Alt board STALE &gt; 120s.</Tip>
        <Tip>Learning weights update only with ≥20 samples — don't force learnings.</Tip>
      </Section>
    </>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.25em] border transition-colors ${
      active
        ? 'bg-cyan-sys text-black border-cyan-sys'
        : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'
    }`}
  >
    <Icon size={12} /> {label}
  </button>
);

export default function Manual() {
  const [tab, setTab] = useState('stock');
  return (
    <div className="p-4 max-w-5xl mx-auto" data-testid="manual-page">
      <CornerPanel title="Operating Manual // revision 6 · iter 110">
        <div className="text-[11px] text-[var(--txt-secondary)] mb-3 uppercase tracking-[0.3em]">
          // all times displayed on this site are CST (america/chicago)
        </div>

        {/* Always-visible operator loop. The ONE thing to memorize. */}
        <MoneyLoop />

        {/* Rules banner — short, blunt, always visible. */}
        <NonNegotiableRules />

        <div className="flex flex-wrap gap-2 mb-4 border-b border-[var(--border-default)] pb-3">
          <TabBtn active={tab === 'stock'} onClick={() => setTab('stock')} icon={Activity} label="Stock Engine" testid="manual-tab-stock" />
          <TabBtn active={tab === 'alt'}   onClick={() => setTab('alt')}   icon={Trophy}   label="Alt Markets"  testid="manual-tab-alt" />
          <TabBtn active={tab === 'flow'}  onClick={() => setTab('flow')}  icon={Cpu}      label="System Flow"  testid="manual-tab-flow" />
          <TabBtn active={tab === 'defs'}  onClick={() => setTab('defs')}  icon={BookOpen} label="Definitions"  testid="manual-tab-defs" />
        </div>

        {tab === 'stock' && <StockEngine />}
        {tab === 'alt'   && <AltMarketsEngine />}
        {tab === 'flow'  && <SystemFlow />}
        {tab === 'defs'  && <Definitions />}
      </CornerPanel>
    </div>
  );
}
